/**
 * Organization List Component
 *
 * Displays a comprehensive list of organizations with:
 * - Search by organization name (min 3 chars via reusable SearchInput)
 * - Filter by organization type
 * - Filter by archive status (Active/Archived)
 * - Pagination with 10 items per page
 * - Edit and Archive action buttons
 * - Archive confirmation dialog
 * - Toast notifications for success/error feedback
 *
 * Add and Edit forms are rendered in the parent view (not modals).
 */

import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useToast } from '@/contexts/ToastContext'
import {
  Edit,
  Trash2,
  Building,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { SearchInput } from '@/components/ui/search-input'
import { ORGANIZATION_TYPE_OPTIONS } from '@/types/organizations'
import type { Organization } from '@/types/organizations'

// ============================================================================
// Component Props
// ============================================================================

export interface OrganizationListProps {
  onOrganizationSelected?: (organization: Organization) => void
  /** Called when the user clicks "+ Add Organization" */
  onAdd?: () => void
  /** Called when the user clicks the Edit button on a row */
  onEdit?: (organization: Organization) => void
}

// ============================================================================
// Component Implementation
// ============================================================================

export function OrganizationList(props: OrganizationListProps) {
  const { onOrganizationSelected, onAdd, onEdit } = props
  const { toast } = useToast()
  // State management
  const [searchQuery, setSearchQuery] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState<string>('')
  const [countryFilter, setCountryFilter] = React.useState<string>('')
  const [currentPage, setCurrentPage] = React.useState(1)

  // Archive dialog state
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = React.useState(false)
  const [selectedOrganization, setSelectedOrganization] = React.useState<Organization | null>(null)

  const itemsPerPage = 10

  // Fetch organizations from API
  const { data, isLoading, isError, refetch } = useApiQuery('getOrganizations', {}, {})

  // Archive mutation
  const archiveOrganization = useApiMutation('archiveOrganization', 'DELETE', {
    onSuccess: () => {
      const action = selectedOrganization?.isArchived ? 'unarchived' : 'archived'
      toast({
        title: 'Success',
        description: `Organization has been ${action} successfully.`,
        type: 'success',
      })
      setIsArchiveDialogOpen(false)
      setSelectedOrganization(null)
      refetch()
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update organization: ${error.message}`,
        type: 'error',
      })
    },
  })

  // Map API response to component state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const organizations: Organization[] = (data?.content?.organizations || []).map((org: any) => {
    log.debug('[OrganizationList] Raw org from API:', org)
    log.debug('[OrganizationList] Extracted fields:', {
      orgNumber: org.orgNumber,
      name: org.name,
      typeof_orgNumber: typeof org.orgNumber
    })
    return {
      id: org.orgNumber?.toString() || '',
      orgNumber: org.orgNumber,
      orgCode: org.orgCode,
      name: org.name,
      type: org.type,
      role: org.role,
      email: org.emailId || org.email,
      emailId: org.emailId,
      phone: org.phone,
      countryCode: org.countryCode,
      mobileNumber: org.mobileNumber,
      address: org.address,
      website: org.website,
      country: org.country,
      taxId: org.taxId,
      accountManagerId: org.accountManagerId,
      employeeCount: org.employeeCount,
      isArchived: !!org.archiveDate || org.isArchived || false,
      archiveDate: org.archiveDate,
      createdAt: org.createdAt,
      updatedAt: org.updatedAt,
    }
  })

  // Apply filters
  const filteredOrganizations = organizations.filter((org) => {
    if (searchQuery && !org.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }
    if (typeFilter && org.type !== typeFilter) {
      return false
    }
    const isUSA = org.country?.toLowerCase().includes('united states') || org.country?.toLowerCase() === 'usa' || org.country?.toLowerCase() === 'us'
    if (countryFilter === 'usa' && !isUSA) {
      return false
    }
    if (countryFilter === 'other' && isUSA) {
      return false
    }
    return true
  })

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, startIndex + itemsPerPage)

  // Handlers
  const handleArchive = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsArchiveDialogOpen(true)
  }

  const handleArchiveConfirm = async () => {
    if (!selectedOrganization) return
    await archiveOrganization.mutateAsync({
      orgNumber: selectedOrganization.orgNumber?.toString() || '',
    })
  }

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, typeFilter, countryFilter])

  // Loading state
  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load organizations</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  const getStatusBadgeVariant = (isArchived: boolean) => {
    return isArchived ? 'secondary' : 'default'
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Organizations</CardTitle>
                <CardDescription className="text-base">Manage organizations</CardDescription>
              </div>
            </div>
            <Button onClick={() => onAdd?.()} className="w-full sm:w-auto">
              + Add Organization
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
          <SearchInput
            placeholder="Search organizations..."
            onSearch={setSearchQuery}
          />
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={typeFilter || 'all'}
              onValueChange={(val) => setTypeFilter(val === 'all' ? '' : val)}
            >
              <SelectTrigger className="w-full sm:w-[250px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ORGANIZATION_TYPE_OPTIONS.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={countryFilter || 'all'}
              onValueChange={(val) => setCountryFilter(val === 'all' ? '' : val)}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="usa">USA</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold text-base">Org Number</TableHead>
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Type</TableHead>
                <TableHead className="font-semibold text-base">Role</TableHead>
                <TableHead className="font-semibold text-base text-center">Status</TableHead>
                <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedOrganizations.length > 0 ? (
                paginatedOrganizations.map((organization) => (
                  <TableRow
                    key={organization.orgNumber ?? organization.orgCode}
                    className="transition-colors hover:bg-muted/50"
                    onClick={onOrganizationSelected ? () => onOrganizationSelected(organization) : undefined}
                  >
                    <TableCell className="text-base text-muted-foreground">
                      {organization.orgNumber ?? '-'}
                    </TableCell>
                    <TableCell className="font-medium text-base">
                      {onOrganizationSelected ? (
                        <span
                          className="hover:underline text-primary cursor-pointer"
                          onClick={() => onOrganizationSelected(organization)}
                        >
                          {organization.name}
                        </span>
                      ) : (
                        <Link
                          to="/organizations/$organizationNumber"
                          params={{ organizationNumber: organization.orgNumber?.toString() ?? '' }}
                          className="hover:underline text-primary"
                        >
                          {organization.name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="text-base">{organization.type}</TableCell>
                    <TableCell className="text-base">{organization.role}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={getStatusBadgeVariant(organization.isArchived)}>
                        {organization.isArchived ? 'Archived' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                          <Link
                            to="/organizations/$organizationNumber/edit"
                            params={{ organizationNumber: organization.orgNumber?.toString() ?? '' }}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit {organization.name}</span>
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleArchive(organization)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Archive {organization.name}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    {searchQuery ? 'No match found.' : 'No organizations found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between border-t py-3 gap-4">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to{' '}
                {Math.min(startIndex + itemsPerPage, filteredOrganizations.length)} of{' '}
                {filteredOrganizations.length} organizations
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archive Confirmation Dialog */}
      <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedOrganization?.isArchived ? 'Unarchive' : 'Archive'} Organization
            </DialogTitle>
            <DialogDescription>
              {selectedOrganization?.isArchived
                ? 'This action will unarchive the organization and all related addresses.'
                : 'This action will archive the organization and all related addresses. Data will remain in the system but marked as archived.'}
            </DialogDescription>
          </DialogHeader>

          {selectedOrganization && (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Organization:</p>
                <p className="text-sm font-medium">{selectedOrganization.name}</p>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsArchiveDialogOpen(false)
                    setSelectedOrganization(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleArchiveConfirm}
                  disabled={archiveOrganization.isPending}
                >
                  {archiveOrganization.isPending
                    ? 'Saving...'
                    : selectedOrganization.isArchived
                      ? 'Unarchive'
                      : 'Archive'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
